import { Badge } from '@artosera/ui';
import { formatDate, locale } from '@artosera/shared';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { EcheanceCoffre } from '../../api/coffre.js';
import { teinteDeLEcheance, urgenceEcheance } from './echeanceUi.js';
import styles from './EcheancesPanel.module.css';

export interface EcheancesPanelProps {
  galerieSlug: string;
  aVenir: readonly EcheanceCoffre[];
  enRetard: readonly EcheanceCoffre[];
  chargement: boolean;
}

/** Tableau de bord des échéances du coffre (3.3, écran 4 ; § 18.7). */
export function EcheancesPanel({
  galerieSlug,
  aVenir,
  enRetard,
  chargement,
}: EcheancesPanelProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'coffre', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';

  if (chargement) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }

  return (
    <div className={styles.enveloppe}>
      <Section
        titre={t('regie:coffre.arborescence.enRetard')}
        echeances={enRetard}
        vide={t('regie:coffre.echeances.aucuneEnRetard')}
        galerieSlug={galerieSlug}
        langue={langue}
      />
      <Section
        titre={t('regie:coffre.arborescence.aVenir')}
        echeances={aVenir}
        vide={t('regie:coffre.echeances.aucuneAVenir')}
        galerieSlug={galerieSlug}
        langue={langue}
      />
    </div>
  );
}

function Section({
  titre,
  echeances,
  vide,
  galerieSlug,
  langue,
}: {
  titre: string;
  echeances: readonly EcheanceCoffre[];
  vide: string;
  galerieSlug: string;
  langue: 'fr' | 'en';
}): JSX.Element {
  const { t } = useTranslation(['coffre']);
  return (
    <section className={styles.section}>
      <div className={styles.enteteSection}>
        <h2 className={styles.titreSection}>{titre}</h2>
        <span className={styles.compte}>{echeances.length}</span>
      </div>
      {echeances.length === 0 ? (
        <p className={styles.vide}>{vide}</p>
      ) : (
        <ul className={styles.liste}>
          {echeances.map((echeance) => {
            const date = new Date(echeance.echeanceLe);
            return (
              <li key={echeance.id}>
                <Link className={styles.ligne} to={`/${galerieSlug}/coffre/${echeance.id}`}>
                  {/* Le feuillet d'éphéméride : jour et mois, redits en clair à droite. */}
                  <span
                    className={styles.feuillet}
                    data-urgence={urgenceEcheance(echeance.joursRestants)}
                    aria-hidden="true"
                  >
                    <span className={styles.feuilletMois}>
                      {new Intl.DateTimeFormat(locale(langue), { month: 'short' }).format(date)}
                    </span>
                    <span className={styles.feuilletJour}>{date.getDate()}</span>
                  </span>
                  <span className={styles.ligneCorps}>
                    <span className={styles.ligneTitre}>{echeance.titre}</span>
                    <span className={styles.ligneMeta}>{t(`coffre:types.${echeance.type}`)}</span>
                  </span>
                  <span className={styles.ligneFin}>
                    <Badge tone={teinteDeLEcheance(echeance.joursRestants)}>
                      {echeance.joursRestants < 0
                        ? t('coffre:echeances.passee', { jours: Math.abs(echeance.joursRestants) })
                        : t('coffre:echeances.dans', { jours: echeance.joursRestants })}
                    </Badge>
                    <span className={styles.ligneDate}>{formatDate(date, langue, 'court')}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
